import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService, {
  AuthUser,
  LoginPayload,
  RegisterPayload,
} from '../services/authService';
import userService from '../services/userService';

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => void;
  requestPasswordReset: (email: string) => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const me = await userService.getCurrentUser();
      const refreshed: AuthUser = {
        ...me,
        id: me.id ?? me._id ?? null,
        email: me.personalData?.email ?? null,
        firstname: me.personalData?.firstname ?? null,
        lastname: me.personalData?.lastname ?? null,
        username: me.username ?? null,
      };
      setUser(refreshed);
      const refreshedToken = authService.getStoredToken();
      if (refreshedToken) setToken(refreshedToken);
      return refreshed;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const storedToken = authService.getStoredToken();
    const storedUser = authService.getStoredUser();
    if (storedToken) {
      setToken(storedToken);
      setUser(storedUser);
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const { token: newToken, user: newUser } = await authService.login(payload);
      setToken(newToken);
      setUser(newUser);
      await refreshUser();
    },
    [refreshUser]
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const { token: newToken, user: newUser } = await authService.register(payload);
      setToken(newToken);
      setUser(newUser);
      await refreshUser();
    },
    [refreshUser]
  );

  const logout = useCallback(() => {
    authService.logout();
    setToken(null);
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await authService.requestPasswordReset(email);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      isLoading,
      login,
      register,
      logout,
      requestPasswordReset,
      refreshUser,
    }),
    [user, token, isLoading, login, register, logout, requestPasswordReset, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden.');
  }
  return ctx;
};

export default AuthContext;
